import Swiper from 'swiper'
import { SwiperOptions } from 'swiper/types/swiper-options'
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules'

const createNavigation = (carousel: HTMLElement) => {
    const navigation: HTMLElement = document.createElement('nav')
    navigation.classList.add('swiper-navigation')

    const buttonPrev: HTMLButtonElement = document.createElement('button')
    buttonPrev.classList.add('swiper-navigation__button', 'swiper-navigation__button-prev')
    buttonPrev.ariaLabel = `{{js.swiper.navigation.slide.previous.aria-label}}`

    const buttonNext: HTMLButtonElement = document.createElement('button')
    buttonNext.classList.add('swiper-navigation__button', 'swiper-navigation__button-next')
    buttonNext.ariaLabel = `{{js.swiper.navigation.slide.next.aria-label}}`

    navigation.appendChild(buttonPrev)
    navigation.appendChild(buttonNext)

    carousel.appendChild(navigation)
}

const createPagination = (carousel: HTMLElement) => {
    const pagination: HTMLElement = document.createElement('nav')
    pagination.classList.add('swiper-pagination')
    carousel.appendChild(pagination)
}

const createCallToAction = (swiper: Swiper) => {
    const container = document.createElement('div')
    container.className = 'swiper-cta'

    const buttons = swiper.el.querySelectorAll<HTMLAnchorElement>('.swiper-slide.attrax-button')
    buttons.forEach((button) => {
        button.remove()
        button.classList.remove('swiper-slide')
        button.removeAttribute('style')
        button.removeAttribute('tabindex')
        button.href = button.href.toLowerCase()
        container.appendChild(button)
    })

    swiper.update()

    // Insert CTA after swiper-wrapper-container if it exists
    const wrapperContainer = swiper.el.querySelector('.swiper-wrapper-container')
    if (wrapperContainer) {
        wrapperContainer.insertAdjacentElement('afterend', container)
    } else {
        swiper.el.appendChild(container)
    }
}

// Creates a wrapper around the swiper-wrapper to allow for additional styling or decoration
const createDecorationWrapper = (carousel: HTMLElement) => {
    const swiperWrapper = carousel.querySelector<HTMLDivElement>('.swiper-wrapper')
    if (!swiperWrapper) return
    const wrapper = document.createElement('div')
    wrapper.classList.add('swiper-wrapper-container')
    wrapper.appendChild(swiperWrapper)
    carousel.appendChild(wrapper)
}

const createCarousel = (query: string, swiperOptions: SwiperOptions) => {
    const carousels = document.querySelectorAll<HTMLElement>(query)
    const swipers: Swiper[] = []

    carousels.forEach((carousel) => {
        carousel.classList.add('swiper')

        const children = [...carousel.children]
        children.forEach((child: HTMLElement) => {
            child.classList.add('swiper-slide')
        })

        if (swiperOptions.navigation !== false) {
            createNavigation(carousel)
            swiperOptions.navigation = {
                prevEl: '.swiper-navigation__button-prev',
                nextEl: '.swiper-navigation__button-next',
                lockClass: 'swiper-navigation__button--lock',
            }
        }

        if (swiperOptions.pagination !== false) {
            createPagination(carousel)
            swiperOptions.pagination = {
                el: '.swiper-pagination',
                clickable: true,
                bulletElement: 'button',
            }
        }

        const swiper = new Swiper(carousel, {
            modules: [Navigation, Pagination, Autoplay, EffectFade],
            createElements: true,
            watchOverflow: true,
            watchSlidesProgress: true,
            ...swiperOptions,
        })

        createDecorationWrapper(carousel)
        swipers.push(swiper)
    })

    return swipers
}

const createPostsCarousel = () => {
    const swipers = createCarousel('.post-carousel .attrax-list-widget__list--has-items', {
        slidesPerView: 1,
        spaceBetween: 40,
        pagination: false,
        breakpoints: {
            666: {
                slidesPerView: 2,
            },
            1200: {
                slidesPerView: 3,
            },
        },
    })

    swipers.forEach(swiper => createCallToAction(swiper))
}

const createScrollingCtaCarousel = () => {
    createCarousel('.corp-scrolling-cta-banner', {
        navigation: false,
        pagination: false,
        loop: true,
        grabCursor: true,
        autoplay: {
            delay: 5000,
            pauseOnMouseEnter: true,
        },
    })
}

const createQuotesCarousel = () => {
    createCarousel('.corp-quotes-banner', {
        navigation: false,
        loop: true,
        grabCursor: true,
        slidesPerView: 1,
        spaceBetween: 30,
        autoplay: {
            delay: 3000,
            pauseOnMouseEnter: true,
        },
    })
}

const createAwardsCarousel = () => {
    createCarousel('.corp-awards-banner', {
        pagination: false,
        loop: true,
        grabCursor: true,
        slidesPerView: 2,
        spaceBetween: 30,
        autoplay: {
            delay: 3000,
            pauseOnMouseEnter: true,
        },
        breakpoints: {
            666: {
                slidesPerView: 4,
            },
        },
    })
}

const createHomepageHeroCarousel = () => {
    createCarousel('.banner-homepage-hero', {
        navigation: false,
        pagination: false,
        loop: true,
        speed: 3000,
        effect: 'fade',
        autoplay: {
            delay: 5000,
            disableOnInteraction: false,
        },
    })
}

export const initialise = () => {
    createPostsCarousel()
    createScrollingCtaCarousel()
    createQuotesCarousel()
    createAwardsCarousel()
    createHomepageHeroCarousel()
}